import React, { useEffect, useMemo, useState } from 'react';
import AuthentificationApi from '../../api/AuthentificationApi';
import '../../fichierCss/FormulaireInscription.css';

interface FormulaireInscriptionProps {
  ouvert: boolean;
  fermeture: () => void;
}

const FormulaireInscription = ({ ouvert, fermeture }: FormulaireInscriptionProps) => {
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [courriel, setCourriel] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmationMotDePasse, setConfirmationMotDePasse] = useState('');
  const [afficherMotDePasse, setAfficherMotDePasse] = useState(false);
  const [afficherConfirmationMotDePasse, setAfficherConfirmationMotDePasse] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  useEffect(() => {
    if (!ouvert) {
      return undefined;
    }

    const gererTouche = (event) => {
      if (event.key === 'Escape') {
        fermeture();
      }
    };

    window.addEventListener('keydown', gererTouche);

    return () => {
      window.removeEventListener('keydown', gererTouche);
    };
  }, [ouvert, fermeture]);

  const motsDePasseConcordent = useMemo(
    () => confirmationMotDePasse.length > 0 && motDePasse === confirmationMotDePasse,
    [motDePasse, confirmationMotDePasse]
  );

  if (!ouvert) {
    return null;
  }

  const soumettreInscription = async (event) => {
    event.preventDefault();
    setErreur('');
    setSucces('');

    if (motDePasse !== confirmationMotDePasse) {
      setErreur('Les mots de passe ne correspondent pas.');
      return;
    }

    setChargement(true);

    try {
      await AuthentificationApi.inscrire({
        nom,
        prenom,
        courriel,
        motDePasse
      });

      setSucces('Compte cree avec succes. Vous pouvez maintenant vous connecter.');

      setTimeout(() => {
        fermeture();
      }, 1000);
    } catch (exception) {
      const estCourrielExistant =
        exception?.message === '409' ||
        exception?.status === 409 ||
        exception?.message?.includes('409');

      if (estCourrielExistant) {
        setErreur('Ce courriel existe deja. Utilisez un autre courriel ou connectez-vous.');
      } else {
        setErreur("Impossible de creer le compte pour l'instant.");
      }
    } finally {
      setChargement(false);
    }
  };

  const afficherAideConcordance = confirmationMotDePasse.length > 0 && !motsDePasseConcordent;

  return (
    <div
      className="inscription-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titre-inscription"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          fermeture();
        }
      }}
    >
      <div className="inscription-modal-card">
        <button
          type="button"
          className="inscription-close"
          onClick={fermeture}
          aria-label="Fermer"
        >
          ×
        </button>

        <div className="inscription-badge">Nouvel espace</div>
        <h2 id="titre-inscription">Creer votre compte</h2>
        <p className="inscription-subtitle">Rejoignez la plateforme en quelques secondes.</p>

        <form className="inscription-form" onSubmit={soumettreInscription}>
          <div className="inscription-form-grid">
            <div>
              <label htmlFor="nom">Nom</label>
              <input
                id="nom"
                type="text"
                value={nom}
                onChange={(event) => setNom(event.target.value)}
                placeholder="Votre nom"
                required
              />
            </div>

            <div>
              <label htmlFor="prenom">Prenom</label>
              <input
                id="prenom"
                type="text"
                value={prenom}
                onChange={(event) => setPrenom(event.target.value)}
                placeholder="Votre prenom"
                required
              />
            </div>
          </div>

          <label htmlFor="courriel-inscription">Courriel</label>
          <input
            id="courriel-inscription"
            type="email"
            value={courriel}
            onChange={(event) => setCourriel(event.target.value)}
            placeholder="nom@domaine.com"
            required
          />

          <label htmlFor="mot-de-passe-inscription">Mot de passe</label>
          <div className="champ-mot-de-passe">
            <input
              id="mot-de-passe-inscription"
              type={afficherMotDePasse ? 'text' : 'password'}
              value={motDePasse}
              onChange={(event) => setMotDePasse(event.target.value)}
              placeholder="Choisissez un mot de passe"
              minLength={8}
              required
            />
            <button
              type="button"
              className="toggle-mdp-inscription"
              onClick={() => setAfficherMotDePasse((precedent) => !precedent)}
              aria-label={afficherMotDePasse ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {afficherMotDePasse ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
          </div>

          <label htmlFor="mot-de-passe-confirmation">Confirmer le mot de passe</label>
          <div className="champ-mot-de-passe">
            <input
              id="mot-de-passe-confirmation"
              type={afficherConfirmationMotDePasse ? 'text' : 'password'}
              value={confirmationMotDePasse}
              onChange={(event) => setConfirmationMotDePasse(event.target.value)}
              placeholder="Retapez votre mot de passe"
              minLength={8}
              required
            />
            <button
              type="button"
              className="toggle-mdp-inscription"
              onClick={() => setAfficherConfirmationMotDePasse((precedent) => !precedent)}
              aria-label={afficherConfirmationMotDePasse ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {afficherConfirmationMotDePasse ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
          </div>

          {afficherAideConcordance && (
            <div className="inscription-feedback erreur">Les mots de passe ne correspondent pas.</div>
          )}
          {erreur && <div className="inscription-feedback erreur">{erreur}</div>}
          {succes && <div className="inscription-feedback succes">{succes}</div>}

          <button
            type="submit"
            className="inscription-submit"
            disabled={chargement || !motsDePasseConcordent}
          >
            {chargement ? 'Creation du compte...' : 'Creer mon compte'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormulaireInscription;
